import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { normalizeCaen } from "@/lib/utils";
import type { EntityData } from "@/types/entity";
import { CURRENT_SESSION_KEY } from "../contexts/SessionSwitcherContext";

type EntityCAENCode =
  Database["public"]["Tables"]["entity_caen_codes"]["Insert"];
type ServiceSelection =
  Database["public"]["Tables"]["service_selections"]["Insert"];
type EntitytDataDB = Database["public"]["Tables"]["entity_data"]["Insert"];

/**
 * Save entity data to database
 */
export async function saveEntityData(
  formSessionId: string,
  entityData: EntityData
): Promise<boolean> {
  try {
    // Save main entity data
    const { data: savedEntity, error: entityError } = await supabase
      .from("entity_data")
      .upsert(
        {
          form_session_id: formSessionId,
          ...(entityData as unknown as EntitytDataDB),
        },
        {
          onConflict: "form_session_id",
        }
      )
      .select()
      .single();

    if (entityError) {
      console.error("Error saving entity data:", entityError);
      return false;
    }

    // Delete existing CAEN codes for this entity
    await supabase
      .from("entity_caen_codes")
      .delete()
      .eq("entity_data_id", savedEntity.id);

    const caenInserts: EntityCAENCode[] = [];
    if (entityData.caen_principal?.cod) {
      caenInserts.push({
        entity_data_id: savedEntity.id,
        cod_caen: entityData.caen_principal.cod,
        denumire_caen: entityData.caen_principal.denumire,
        is_primary: true,
      });
    }
    if (entityData.caen_secundar && entityData.caen_secundar.length > 0) {
      caenInserts.push(
        ...entityData.caen_secundar.map((caen: any) => ({
          entity_data_id: savedEntity.id,
          cod_caen: caen?.cod || caen,
          denumire_caen: caen?.denumire || "",
          is_primary: false,
        }))
      );
    }

    if (caenInserts.length > 0) {
      const { error: caenError } = await supabase
        .from("entity_caen_codes")
        .insert(caenInserts);

      if (caenError) {
        console.error("Error saving CAEN codes:", caenError);
        return false;
      }
    }
    return true;
  } catch (error) {
    console.error("Error saving entity data:", error);
    return false;
  }
}

export async function deleteEntityData(formSessionId: string) {
  try {
    await supabase
      .from("entity_data")
      .delete()
      .eq("form_session_id", formSessionId);
    return true;
  } catch (error) {
    console.error("Error deleting entity data:", error);
    return false;
  }
}

/**
 * Save service selections to database
 */
export async function saveServiceSelections(
  formSessionId: string,
  selectedServices: string[],
  allServices: any[]
): Promise<boolean> {
  try {
    // Delete existing service selections
    await supabase
      .from("service_selections")
      .delete()
      .eq("form_session_id", formSessionId);

    // Save new service selections
    const serviceInserts: ServiceSelection[] = allServices.map((service) => ({
      form_session_id: formSessionId,
      service_code: service.code,
      service_name: service.name,
      caen_codes: service.caenCodes || [],
      is_selected: selectedServices.includes(service.code),
    }));

    const { error } = await supabase
      .from("service_selections")
      .insert(serviceInserts);

    if (error) {
      console.error("Error saving service selections:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error saving service selections:", error);
    return false;
  }
}

/**
 * Save evaluation result to database
 */
export async function saveEvaluation(
  formSessionId: string,
  evaluationType: string,
  result: string,
  criteria: any,
  requiresImpactAnalysis = false,
  impactAnalysisResult?: string
): Promise<boolean> {
  try {
    const { error } = await supabase.from("evaluations").upsert({
      form_session_id: formSessionId,
      evaluation_type: evaluationType,
      result,
      criteria,
      requires_impact_analysis: requiresImpactAnalysis,
      impact_analysis_result: impactAnalysisResult,
    });

    if (error) {
      console.error("Error saving evaluation:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error saving evaluation:", error);
    return false;
  }
}

/**
 * Save step progress to database
 */
export async function saveStepProgress(
  formSessionId: string,
  stepNumber: number,
  stepName: string,
  substepNumber = 0,
  substepName?: string,
  stepData?: any
): Promise<boolean> {
  try {
    const { error } = await supabase.from("step_progress").upsert(
      {
        form_session_id: formSessionId,
        step_number: stepNumber,
        step_name: stepName,
        substep_number: substepNumber,
        substep_name: substepName,
        status: "completed",
        completed_at: new Date().toISOString(),
        step_data: stepData,
      },
      {
        onConflict: "form_session_id,step_number,substep_number",
      }
    );

    if (error) {
      console.error("Error saving step progress:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error saving step progress:", error);
    return false;
  }
}

/**
 * Load session data from database
 */
export async function loadSessionData(formSessionId: string) {
  try {
    // Get form session
    const { data: session, error: sessionError } = await supabase
      .from("form_sessions")
      .select("*")
      .eq("id", formSessionId)
      .single();

    if (sessionError) {
      console.error("Error loading session:", sessionError);
      // remove session if from local storage
      localStorage.removeItem(CURRENT_SESSION_KEY);
      return null;
    }

    // Get entity data (may not exist for new sessions)
    const { data: entityData, error: entityError } = await supabase
      .from("entity_data")
      .select("*")
      .eq("form_session_id", formSessionId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (entityError) {
      console.error("Error loading entity data:", entityError);
    }

    // Get service selections
    const { data: serviceSelections, error: serviceError } = await supabase
      .from("service_selections")
      .select("*")
      .eq("form_session_id", formSessionId);

    if (serviceError) {
      console.error("Error loading service selections:", serviceError);
    }

    // Get evaluations
    const { data: evaluations, error: evaluationError } = await supabase
      .from("evaluations")
      .select("*")
      .eq("form_session_id", formSessionId);

    if (evaluationError) {
      console.error("Error loading evaluations:", evaluationError);
    }

    // Get step progress
    const { data: stepProgress, error: progressError } = await supabase
      .from("step_progress")
      .select("*")
      .eq("form_session_id", formSessionId)
      .order("step_number", { ascending: true });

    if (progressError) {
      console.error("Error loading step progress:", progressError);
    }

    // Transform data to wizard-expected format
    const selectedServices =
      serviceSelections
        ?.filter((s) => s.is_selected)
        .map((s) => s.service_code) || [];

    const entitySize = entityData?.entity_size as
      | "MICĂ"
      | "MIJLOCIE"
      | "MARE"
      | undefined;

    const financialData = entityData
      ? {
          nrSalariati: entityData.nr_salariati || 0,
          cifraAfaceri: entityData.cifra_afaceri || 0,
          active: entityData.active || 0,
        }
      : null;

    // Return flattened structure that matches wizard expectations
    return {
      session,
      entityData: entityData as unknown as EntityData,
      serviceSelections,
      evaluations,
      stepProgress,
      // Flattened data for wizard state
      currentStep: session.current_step,
      currentSubstep: session.current_substep,
      selectedServices,
      entitySize,
      financialData,
      entity_data: entityData,
    };
  } catch (error) {
    console.error("Error loading session data:", error);
    return null;
  }
}

export async function loadFinancialData(cui: string) {
  // First, try to fetch from Supabase
  try {
    const { data: supabaseData, error: supabaseError } =
      await supabase.functions.invoke(
        `api/financial-data?cui=${encodeURIComponent(cui)}`,
        {
          method: "GET",
        }
      );

    if (supabaseError) {
      console.error(
        "Error loading financial data from Supabase:",
        supabaseError
      );
      return null;
    }
    if (supabaseData) {
      const row = { ...supabaseData, caen: normalizeCaen(supabaseData?.caen) };
      return { data: row, error: supabaseError };
    }
    return { data: null, error: supabaseError };
  } catch (error) {
    console.error("Error loading financial data:", error);
    return null;
  }
}
